"use client"

import { useState, useEffect } from "react"
import * as XLSX from 'xlsx'
import PageMeta from "../../components/common/PageMeta"
import PageBreadcrumb from "../../components/common/PageBreadCrumb"
import Button from "../../components/ui/button/Button"
import { Modal } from "../../components/ui/modal"
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../components/ui/table"
import Badge from "../../components/ui/badge/Badge"
import useApi from "../../hooks/useApi"
import CreateExcursionModal from "../../components/excursions/CreateExcursionModal"
import { toast } from "react-hot-toast"
import useAuth from "../../hooks/useAuth"
import RoleBasedRenderer from "../../components/auth/RoleBasedRenderer"
import { Tag, Book, DollarSign, Users, MapPin, CheckCircle, Eye, Edit, Trash2, Power, PowerOff } from "lucide-react"
import Input from "../../components/form/input/InputField"
import CustomPriceModal from "../../components/excursions/CustomPriceModal"

type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark"

export interface Excursion {
  id: number
  name: string
  description: string
  priceB2CAdult: number
  priceB2CChild?: number | null
  priceB2BAdult: number
  priceB2BChild?: number | null
  maxCapacity: number
  booked: number
  active: boolean
  destination: string
  isThirdParty: boolean
}

interface NewExcursionForm {
  name: string
  description: string
  priceB2CAdult: string
  priceB2CChild?: string
  priceB2BAdult: string
  priceB2BChild?: string
  maxCapacity: string
  active: boolean
  isThirdParty: boolean
}

interface FormErrors {
  name?: string;
  description?: string;
  priceB2CAdult?: string;
  priceB2BAdult?: string;
  maxCapacity?: string;
}

export default function Excursiones() {
  const { getExcursiones, createExcursion, updateExcursion, deleteExcursion } = useApi()
  const { canCreate, canEdit, canDelete } = useAuth()

  const [excursions, setExcursions] = useState<Excursion[]>([])
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [editingExcursion, setEditingExcursion] = useState<Excursion | null>(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [detailsExcursion, setDetailsExcursion] = useState<Excursion | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all")
  const [isLoading, setIsLoading] = useState(true)
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    priceB2CAdult: "",
    priceB2CChild: "",
    priceB2BAdult: "",
    priceB2BChild: "",
    maxCapacity: "",
    active: true,
    destination: "",
    isThirdParty: false
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [excursionToDelete, setExcursionToDelete] = useState<Excursion | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomPriceModalOpen, setIsCustomPriceModalOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of excursions to show per page

  useEffect(() => {
    const fetchExcursions = async () => {
      try {
        setIsLoading(true)
        const data = await getExcursiones()
        setExcursions(data)
        setIsLoading(false)
      } catch (error) {
        toast.error("Error obteniendo excursiones")
        setIsLoading(false)
      }
    }

    fetchExcursions()
  }, [getExcursiones])

  useEffect(() => {
    if (!isDetailsModalOpen) {
      setDetailsExcursion(null)
    }
  }, [isDetailsModalOpen])

  useEffect(() => {
    if (editingExcursion) {
      setEditFormData({
        name: editingExcursion.name,
        description: editingExcursion.description,
        priceB2CAdult: editingExcursion.priceB2CAdult.toString(),
        priceB2CChild: editingExcursion.priceB2CChild?.toString() ?? "",
        priceB2BAdult: editingExcursion.priceB2BAdult.toString(),
        priceB2BChild: editingExcursion.priceB2BChild?.toString() ?? "",
        maxCapacity: editingExcursion.maxCapacity.toString(),
        active: editingExcursion.active,
        destination: editingExcursion.destination || "",
        isThirdParty: editingExcursion.isThirdParty
      });
    }
  }, [editingExcursion]);

  const filteredExcursions = excursions.filter((exc) => {
    const matchesSearch =
      exc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exc.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesActive =
      activeFilter === "all" ||
      (activeFilter === "active" && exc.active) ||
      (activeFilter === "inactive" && !exc.active)
    return matchesSearch && matchesActive
  })

  // Calculate pagination values
  const totalPages = Math.ceil(filteredExcursions.length / itemsPerPage);
  const paginatedExcursions = filteredExcursions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter]);

  // Pagination controls
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const openAddModal = () => {
    setEditingExcursion(null)
    setIsFormModalOpen(true)
  }

  const openEditModal = (exc: Excursion) => {
    setEditingExcursion(exc)
    setIsFormModalOpen(true)
  }

  const handleDeleteExcursion = async (excursion: Excursion) => {
    setExcursionToDelete(excursion);
    setIsDeleteModalOpen(true);
  };

  const executeDelete = async () => {
    if (!excursionToDelete) return;
    
    try {
      await deleteExcursion(excursionToDelete.id);
      setExcursions((prev) => prev.filter((e) => e.id !== excursionToDelete.id));
      toast.success("Excursión eliminada exitosamente");
      setIsDeleteModalOpen(false);
      setExcursionToDelete(null);
    } catch (error) {
      toast.error("Error al eliminar la excursión");
    }
  };

  const toggleActive = async (id: number) => {
    try {
      const excursion = excursions.find((e) => e.id === id)
      if (!excursion) return
      const updatedExcursion: Excursion = { ...excursion, active: !excursion.active }
      const updated = await updateExcursion(updatedExcursion)
      setExcursions((prev) => prev.map((e) => (e.id === id ? updated : e)))
    } catch (error) {
      toast.error("Error al actualizar la excursión");
    }
  }

  const openDetailsModal = (exc: Excursion) => {
    setDetailsExcursion(exc)
    setIsDetailsModalOpen(true)
  }

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false)
  }

  const getActiveBadge = (active: boolean) => ({
    color: active ? 'success' : 'error' as BadgeColor,
    text: active ? 'Activa' : 'Inactiva'
  });

  const validateEditForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!editFormData.name.trim()) {
      newErrors.name = 'El nombre de la excursión es requerido';
    }
    
    if (!editFormData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    }
    
    if (!editFormData.priceB2CAdult || parseFloat(editFormData.priceB2CAdult) <= 0) {
      newErrors.priceB2CAdult = 'Ingrese un precio válido mayor a 0';
    }
    
    if (!editFormData.priceB2BAdult || parseFloat(editFormData.priceB2BAdult) <= 0) {
      newErrors.priceB2BAdult = 'Ingrese un precio válido mayor a 0';
    }
    
    if (!editFormData.maxCapacity || parseInt(editFormData.maxCapacity) <= 0) {
      newErrors.maxCapacity = 'La capacidad máxima debe ser mayor a 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEditForm() || !editingExcursion) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const updatedExcursion = await updateExcursion({
        id: editingExcursion.id,
        booked: editingExcursion.booked,
        name: editFormData.name,
        description: editFormData.description,
        priceB2CAdult: Number(editFormData.priceB2CAdult),
        priceB2CChild: editFormData.priceB2CChild === "" ? null : Number(editFormData.priceB2CChild),
        priceB2BAdult: Number(editFormData.priceB2BAdult),
        priceB2BChild: editFormData.priceB2BChild === "" ? null : Number(editFormData.priceB2BChild),
        maxCapacity: Number(editFormData.maxCapacity),
        active: editFormData.active,
        destination: editFormData.destination,
        isThirdParty: editFormData.isThirdParty
      });
      
      setExcursions(prev => prev.map(e => e.id === updatedExcursion.id ? updatedExcursion : e));
      setEditingExcursion(null);
      setIsFormModalOpen(false);
      toast.success('Excursión actualizada exitosamente');
    } catch (error) {
      console.error("Error al actualizar excursión:", error);
      toast.error("Error al actualizar la excursión");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportToExcel = () => {
    // Preparar los datos para Excel
    const excelData = filteredExcursions.map(exc => ({
      'Nombre': exc.name,
      'Descripción': exc.description,
      'Destino': exc.destination,
      'Precio B2C Adulto': exc.priceB2CAdult,
      'Precio B2C Niño': exc.priceB2CChild,
      'Precio B2B Adulto': exc.priceB2BAdult,
      'Precio B2B Niño': exc.priceB2BChild,
      'Capacidad Máxima': exc.maxCapacity,
      'Ocupación Actual': exc.booked,
      'Estado': exc.active ? 'Activa' : 'Inactiva',
      'Terceros': exc.isThirdParty ? 'Terceros' : 'Propia'
    }));

    // Crear libro y hoja de Excel
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Excursiones');

    // Generar el nombre del archivo con la fecha actual
    const date = new Date();
    const fileName = `excursiones_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}.xlsx`;

    // Guardar el archivo
    XLSX.writeFile(wb, fileName);
    toast.success('Excursiones exportadas exitosamente');
  };

  return (
    <div className="dark:bg-gray-900">
      <PageMeta
        title="Gestión de excursiones | Destiny Caribbean Tours"
        description="Administración completa de las excursiones."
      />
      <PageBreadcrumb pageTitle="Excursiones" />

      <div className="min-h-screen rounded-2xl border border-gray-200 bg-white px-4 py-6 dark:border-gray-700 dark:bg-gray-800 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Administración de excursiones</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Lista de excursiones activas/inactivas</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 sm:w-48"
            />
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as "all" | "active" | "inactive")}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:bg-gray-700 focus:border-blue-500 focus:ring-blue-500 sm:w-32"
            >
              <option value="all" className="dark:bg-gray-700">Todas</option>
              <option value="active" className="dark:bg-gray-700">Activas</option>
              <option value="inactive" className="dark:bg-gray-700">Inactivas</option>
            </select>
            <div className="flex gap-2">
              <RoleBasedRenderer requiredPermission="create">
                <Button variant="primary" onClick={openAddModal} className="w-full sm:w-auto">
                  Agregar nueva
                </Button>
              </RoleBasedRenderer>
              <RoleBasedRenderer requiredPermission="edit">
                <Button variant="secondary" onClick={() => setIsCustomPriceModalOpen(true)} className="w-full sm:w-auto">
                  Precios personalizados
                </Button>
              </RoleBasedRenderer>
              <Button variant="outline" onClick={handleExportToExcel} className="w-full sm:w-auto">
                Exportar Excel
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-800">
              <TableRow>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  Nombre
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  B2C Adulto
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  B2C Niño
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  B2B Adulto
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  B2B Niño
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  Ocupación
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  Estado
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  Terceros
                </TableCell>
                <TableCell
                  isHeader
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 sm:px-6"
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                    Cargando excursiones...
                  </TableCell>
                </TableRow>
              ) : filteredExcursions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                    {searchTerm
                      ? `No se encontraron resultados para "${searchTerm}"`
                      : "No hay excursiones registradas"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedExcursions.map((exc) => (
                  <TableRow key={exc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6 min-w-[200px] max-w-[300px]">
                      <div className="truncate" title={exc.name}>
                        {exc.name}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                      ${exc.priceB2CAdult}
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                      {exc.priceB2CChild !== null && exc.priceB2CChild !== undefined
                        ? `$${exc.priceB2CChild}`
                        : <span className="text-gray-500 dark:text-gray-400">No aplica</span>
                      }
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                      ${exc.priceB2BAdult}
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                      {exc.priceB2BChild !== null && exc.priceB2BChild !== undefined
                        ? `$${exc.priceB2BChild}`
                        : <span className="text-gray-500 dark:text-gray-400">No aplica</span>
                      }
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm sm:px-6">
                      <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full"
                          style={{
                            width: `${(exc.booked / exc.maxCapacity) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {exc.booked}/{exc.maxCapacity}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm sm:px-6">
                      <Badge variant="light" color={getActiveBadge(exc.active).color}>
                        {getActiveBadge(exc.active).text}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm sm:px-6">
                      <Badge variant="light" color={exc.isThirdParty ? "warning" : "success"}>
                        {exc.isThirdParty ? "Terceros" : "Propia"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm sm:px-6">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => openDetailsModal(exc)}
                          className="p-2"
                          aria-label="Ver detalles"
                        >
                          <Eye size={16} />
                        </Button>
                        <RoleBasedRenderer requiredPermission="edit">
                          <Button
                            variant="outline"
                            onClick={() => openEditModal(exc)}
                            className="p-2"
                            aria-label="Editar"
                          >
                            <Edit size={16} />
                          </Button>
                        </RoleBasedRenderer>
                        <RoleBasedRenderer requiredPermission="edit">
                          {exc.active ? (
                            <Button
                              variant="outline"
                              onClick={() => toggleActive(exc.id)}
                              className="p-2"
                              aria-label="Desactivar"
                            >
                              <PowerOff size={16} />
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() => toggleActive(exc.id)}
                              className="p-2"
                              aria-label="Activar"
                            >
                              <Power size={16} />
                            </Button>
                          )}
                        </RoleBasedRenderer>
                        <RoleBasedRenderer requiredPermission="delete">
                          <Button
                            variant="outline"
                            onClick={() => handleDeleteExcursion(exc)}
                            className="p-2"
                            aria-label="Eliminar"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </RoleBasedRenderer>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination controls */}
        {filteredExcursions.length > itemsPerPage && (
          <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                  ${currentPage === 1 
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                  border border-gray-300 dark:border-gray-600`}
              >
                Anterior
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                  ${currentPage === totalPages 
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                  border border-gray-300 dark:border-gray-600`}
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredExcursions.length)}</span> a{" "}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredExcursions.length)}</span> de{" "}
                  <span className="font-medium">{filteredExcursions.length}</span> excursiones
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                      ${currentPage === 1 
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    <span className="sr-only">Anterior</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers - limited to 5 pages */}
                  {(() => {
                    // Calculate which page numbers to show (max 5)
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + 4);
                    
                    // Adjust startPage if we're near the end
                    if (endPage - startPage < 4 && startPage > 1) {
                      startPage = Math.max(1, endPage - 4);
                    }
                    
                    const pages = [];
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                            ${currentPage === i
                              ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                      ${currentPage === totalPages 
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    <span className="sr-only">Siguiente</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal para agregar/editar excursiones */}
      {isFormModalOpen && (
        <CreateExcursionModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onAddExcursion={editingExcursion ? 
            // Si estamos editando, envolvemos updateExcursion para que coincida con la firma esperada
            async (excursion) => updateExcursion({ ...excursion, id: editingExcursion.id, booked: editingExcursion.booked }) :
            createExcursion
          }
          onSuccess={(excursion) => {
            if (editingExcursion) {
              setExcursions((prev) => prev.map((e) => (e.id === excursion.id ? excursion : e)));
            } else {
              setExcursions((prev) => [...prev, excursion]);
            }
          }}
        />
      )}

      {editingExcursion && (
        <Modal
          isOpen={!!editingExcursion}
          onClose={() => {
            setEditingExcursion(null);
            setIsFormModalOpen(false);
          }}
          title="Editar excursión"
        >
          <form onSubmit={handleEditSubmit} className="space-y-6">
            {/* Información Básica */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <Tag className="w-4 h-4 mr-1.5 text-primary-500" />
                Información Básica
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {/* Nombre */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Nombre
                    <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Nombre de la excursión"
                    error={errors.name ? true : false}
                    hint={errors.name}
                    className="w-full"
                  />
                </div>
                
                {/* Descripción */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descripción
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Descripción detallada de la excursión"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md ${
                      errors.description 
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                    } dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
                  />
                  {errors.description && (
                    <p className="text-xs text-red-500">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Precios y Capacidad */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <DollarSign className="w-4 h-4 mr-1.5 text-primary-500" />
                Precios y Capacidad
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Precios B2C */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Precios B2C (Cliente Final)
                  </label>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      name="priceB2CAdult"
                      value={editFormData.priceB2CAdult}
                      onChange={(e) => setEditFormData({ ...editFormData, priceB2CAdult: e.target.value })}
                      placeholder="Precio Adulto"
                      step="0.01"
                      min="0"
                      error={errors.priceB2CAdult ? true : false}
                      hint={errors.priceB2CAdult}
                      className="w-full"
                    />
                    <Input
                      type="number"
                      name="priceB2CChild"
                      value={editFormData.priceB2CChild}
                      onChange={(e) => setEditFormData({ ...editFormData, priceB2CChild: e.target.value })}
                      placeholder="Precio Niño"
                      step="0.01"
                      min="0"
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Dejar en blanco si la excursión no admite niños
                    </p>
                  </div>
                </div>

                {/* Precios B2B */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Precios B2B (Agencias)
                  </label>
                  <div className="space-y-2">
                    <Input
                      type="number"
                      name="priceB2BAdult"
                      value={editFormData.priceB2BAdult}
                      onChange={(e) => setEditFormData({ ...editFormData, priceB2BAdult: e.target.value })}
                      placeholder="Precio Adulto"
                      step="0.01"
                      min="0"
                      error={errors.priceB2BAdult ? true : false}
                      hint={errors.priceB2BAdult}
                      className="w-full"
                    />
                    <Input
                      type="number"
                      name="priceB2BChild"
                      value={editFormData.priceB2BChild}
                      onChange={(e) => setEditFormData({ ...editFormData, priceB2BChild: e.target.value })}
                      placeholder="Precio Niño"
                      step="0.01"
                      min="0"
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Dejar en blanco si la excursión no admite niños
                    </p>
                  </div>
                </div>
              </div>

              {/* Capacidad y Destino */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Capacidad máxima
                    <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    name="maxCapacity"
                    value={editFormData.maxCapacity}
                    onChange={(e) => setEditFormData({ ...editFormData, maxCapacity: e.target.value })}
                    placeholder="Número de personas"
                    min="1"
                    error={errors.maxCapacity ? true : false}
                    hint={errors.maxCapacity}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Destino
                  </label>
                  <Input
                    type="text"
                    name="destination"
                    value={editFormData.destination}
                    onChange={(e) => setEditFormData({ ...editFormData, destination: e.target.value })}
                    placeholder="Ej: Saona, Catalina, etc."
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Configuración */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1.5 text-primary-500" />
                Configuración
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Estado */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    Estado
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="active-edit"
                      name="active"
                      checked={editFormData.active}
                      onChange={(e) => setEditFormData({ ...editFormData, active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label htmlFor="active-edit" className="text-sm text-gray-700 dark:text-gray-300">
                      Activa
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Las excursiones inactivas no aparecerán en los listados públicos
                  </p>
                </div>

                {/* Tipo de Excursión */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tipo de Excursión
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isThirdParty-edit"
                      name="isThirdParty"
                      checked={editFormData.isThirdParty}
                      onChange={(e) => setEditFormData({ ...editFormData, isThirdParty: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label htmlFor="isThirdParty-edit" className="text-sm text-gray-700 dark:text-gray-300">
                      Excursión de terceros
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Marque esta opción si la excursión es operada por un tercero
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingExcursion(null);
                  setIsFormModalOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal para ver detalles */}
      {isDetailsModalOpen && detailsExcursion && (
        <Modal
          key={`details-${detailsExcursion.id}`}
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
          title={detailsExcursion.name}
          size="md"
        >
          <div className="space-y-6">
            {/* Descripción */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Descripción
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {detailsExcursion.description}
              </p>
            </div>

            {/* Precios B2C */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Precios B2C (Cliente final)
                </h3>
              </div>
              <div className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Adulto</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    ${detailsExcursion.priceB2CAdult}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Niño</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {detailsExcursion.priceB2CChild !== null && detailsExcursion.priceB2CChild !== undefined
                      ? `$${detailsExcursion.priceB2CChild}`
                      : <span className="text-gray-400 text-base">No disponible para niños</span>
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Precios B2B */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Precios B2B (Agencias)
                </h3>
              </div>
              <div className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Adulto</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    ${detailsExcursion.priceB2BAdult}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Niño</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {detailsExcursion.priceB2BChild !== null && detailsExcursion.priceB2BChild !== undefined
                      ? `$${detailsExcursion.priceB2BChild}`
                      : <span className="text-gray-400 text-base">No disponible para niños</span>
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Capacidad y Estado */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Capacidad
                </h3>
                <div className="flex items-end gap-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {detailsExcursion.booked}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 mb-1">/</span>
                  <span className="text-lg text-gray-500 dark:text-gray-400 mb-0.5">
                    {detailsExcursion.maxCapacity}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Lugares ocupados
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Estado
                </h3>
                <div className="text-sm">
                  <Badge
                    color={detailsExcursion.active ? "success" : "error"}
                  >
                    {detailsExcursion.active ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Estado actual
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="primary" onClick={closeDetailsModal}>
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setExcursionToDelete(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar esta excursión?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados a la excursión:
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {excursionToDelete?.name}
                </h3>
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <li>Información de la excursión</li>
                  <li>Historial de reservas asociadas</li>
                  <li>Configuración de precios</li>
                  <li>Datos de capacidad y ocupación</li>
                </ul>
              </div>

              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setExcursionToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={executeDelete}
            >
              Sí, eliminar excursión
            </Button>
          </div>
        </div>
      </Modal>

      {/* Custom Price Modal */}
      <CustomPriceModal
        isOpen={isCustomPriceModalOpen}
        onClose={() => setIsCustomPriceModalOpen(false)}
      />
    </div>
  )
}